import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import * as tus from 'tus-js-client';
import { supabase } from '../lib/supabase.ts';
import { callApi } from '../lib/api.ts';
import { withRetry, runPool } from '../lib/uploadPool.ts';
import {
  subscribe as subscribeUploads,
  getSnapshot as getUploadSnapshot,
  ensureSession,
  setItems as setUploadItems,
  patchItem as patchUploadItem,
  setBusy as setUploadBusy,
  uploadCtx,
  beginInFlight,
  endInFlight,
  type UploadItem,
} from '../lib/uploadSession.ts';
import { Toast, type ToastState } from './Toast.tsx';
import type { Client } from '../lib/types.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import { btnSolid } from '../lib/ui.ts';
import { FlapText } from './FlapText.tsx';

// #GR9g: cap simultaneous uploads (never fire a 296-image batch at once). 4 is fast
// (~4× sequential) while staying under Cloudflare Images / function rate-limits.
const UPLOAD_CONCURRENCY = 4;

/**
 * Content & media uploader (content-media.md M2). Drag-drop images + video + a
 * manifest; per file: media-upload-url → browser PUTs bytes straight to Cloudflare →
 * media-create (with the manifest's alt). Video then finalizes to MP4-ready (rule 18)
 * via media-finalize-video. Catalogue view: thumb · kind · alt (editable) · role,
 * with re-order. Mixed media first-class (photo-only / video-only / both). All writes
 * are scoped + is_live-blocked server-side; the step marks done on ≥1 catalogued asset.
 */

interface CatRow {
  id: string;
  kind: 'image' | 'video';
  url: string | null;
  poster: string | null;
  alt: string;
  role: string | null;
  stream_uid: string | null;
  sort_order: number;
  original_filename: string | null;
}

// The queue item shape lives in the module-level upload session (survives remounts).
type QueueItem = UploadItem;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Resumable video upload → the server-created Cloudflare Stream tus URL (Location). tus-js-client
// chunks the file (50 MB) + auto-resumes on a dropped connection (retryDelays), so a >200 MB / 2 GB
// master uploads reliably; a terminal failure (retries exhausted) rejects with the real error (the
// #GR9g honest-failure fix at the >200 MB boundary). No token client-side — the URL is one-time.
function uploadVideoTus(uploadUrl: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      uploadUrl, // resume to the pre-created tus resource (no POST-create, no token)
      chunkSize: 52_428_800, // 50 MB — must be a multiple of 256 KiB (Cloudflare requires equal chunks)
      retryDelays: [0, 3_000, 5_000, 10_000, 20_000], // (c) built-in resume on a dropped connection — same
      // upload URL, so a wifi drop resumes from the last chunk instead of orphaning a reservation.
      storeFingerprintForResuming: true, // persist the tus offset (localStorage) so a re-selected file resumes
      removeFingerprintOnSuccess: true, // …and clean it up once done (no stale resume markers)
      onProgress: (sent, total) => onProgress(total ? Math.round((sent / total) * 100) : 0),
      onSuccess: () => resolve(),
      onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
    });
    upload.start();
  });
}

/** Read an image's intrinsic dims client-side (#11a) — deterministic, no network. So
 *  the catalogue stores width/height and aspect-true thumbs + orientation chips work
 *  for images (video dims come from Stream). Best-effort: null if the decode fails. */
async function imageDims(file: File): Promise<{ width: number; height: number } | null> {
  try {
    const bmp = await createImageBitmap(file);
    const dims = { width: bmp.width, height: bmp.height };
    bmp.close();
    return dims.width && dims.height ? dims : null;
  } catch {
    return null;
  }
}
const kindOf = (f: File): 'image' | 'video' | null =>
  f.type.startsWith('image/') ? 'image' : f.type.startsWith('video/') ? 'video' : null;

export function MediaUploader({
  client,
  version,
  onIngested,
  onCatalogueChange,
  hideCatalogue,
  canMaintainLive = false,
}: {
  client: Client;
  /** Shared media version — reload the catalogue/groups when a sibling view changes it (#12). */
  version?: number;
  onIngested: () => void;
  onCatalogueChange?: () => void;
  /** Suppress this component's own "Catalogue" list. When the uploader is embedded above
   *  the structuring galleries + pool (the single canonical media view), showing its
   *  catalogue too would list every image twice. Drop-zone + progress + how-to still show. */
  hideCatalogue?: boolean;
  /** go-live-protection §3a: an admin maintaining a maintainable live client — the backend
   *  liveGuard('site-write') allow-condition. True → uploads work (edit lands on the preview). */
  canMaintainLive?: boolean;
}) {
  // Disabled only when live AND NOT the admin maintenance carve-out (matches the backend gate).
  const liveBlocked = client.is_live && !canMaintainLive;
  const [catalogue, setCatalogue] = useState<CatRow[]>([]);
  // (b) The upload queue + busy live in a MODULE-LEVEL session (uploadSession.ts), not component
  // state, so a background remount/re-render doesn't wipe the progress panel or the in-flight
  // upload mid-op. We subscribe to it; a remount with the same client re-attaches (ensureSession).
  const session = useSyncExternalStore(subscribeUploads, getUploadSnapshot, getUploadSnapshot);
  const forThisClient = session.clientId === client.id;
  const queue = forThisClient ? session.items : [];
  const busy = forThisClient && session.busy;
  const [toast, setToast] = useState<ToastState | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Pre-assign uploads to a gallery (#8): the chosen group for this batch ('' = loose pool).
  const [groups, setGroups] = useState<{ id: string; name: string; kind: string }[]>([]);
  const [uploadGroupId, setUploadGroupId] = useState('');
  const ingestedRef = useRef(false);
  // #GR9g: per-item upload context (the File + kind + target gallery), keyed by queue key, so
  // "Retry failed" can re-upload EXACTLY the failed items. Module-level (uploadCtx) so a remount
  // doesn't lose it. setItem/setQueue write THROUGH to the session store.
  const itemCtx = uploadCtx;
  const setQueue = setUploadItems;
  const setItem = patchUploadItem;
  const setBusy = setUploadBusy;
  // In-flight guard so a rapid double-click on 🗑 can't fire two deletes (the ref is
  // synchronous — state alone is stale within the same click burst).
  const deletingRef = useRef(false);
  const [deleting, setDeleting] = useState(false);

  const loadCatalogue = useCallback(async () => {
    const { data } = await supabase
      .from('media')
      .select('id, kind, url, poster, alt, role, stream_uid, sort_order, original_filename')
      .eq('client_id', client.id)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    setCatalogue((data as CatRow[] | null) ?? []);
  }, [client.id]);

  const loadGroups = useCallback(async () => {
    const { data } = await supabase
      .from('media_groups')
      .select('id, name, kind')
      .eq('client_id', client.id)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    setGroups((data as { id: string; name: string; kind: string }[] | null) ?? []);
  }, [client.id]);

  useEffect(() => {
    void loadCatalogue();
    void loadGroups();
    // `version` is intentionally in the deps: a sibling view (structuring) bumping it
    // refreshes this catalogue without a manual reload (#12).
  }, [loadCatalogue, loadGroups, version]);

  // (b) Attach to (or start) this client's upload session on mount / client change. A remount with
  // the SAME client re-attaches to an in-flight session (the panel rehydrates); switching clients
  // clears the UI for the new one. #GR9f is now a MODULE-LEVEL beforeunload guard in uploadSession
  // (fires whenever any upload is in flight — a remount can't silently drop it like the old
  // component-`busy` effect could).
  useEffect(() => {
    ensureSession(client.id);
  }, [client.id]);

  // The per-file pipeline: mint URL → PUT to Cloudflare → catalogue → (video) finalize.
  // Slice 1d CSV unification: the uploader no longer reads a manifest at upload time — alt /
  // title / SEO metadata are set by the single "Apply metadata from a CSV" button (match by
  // filename, works photo + video, any time), so there's ONE CSV door, not two.
  async function ingestOne(file: File, kind: 'image' | 'video', key: string, groupId: string) {
    // Capture image dims up front (#11a) — video dims come from Stream server-side.
    const dims = kind === 'image' ? await imageDims(file) : null;
    setItem(key, { status: 'uploading', progress: 0 });
    let assetId: string;
    if (kind === 'video') {
      // Video → tus resumable (kills the 200 MB single-POST wall; 2 GB masters OK). Mint the
      // one-time tus URL (retry the cheap mint on a transient blip), then upload with tus-js-client
      // — its OWN chunk-level resume, so we do NOT re-mint/re-wrap in withRetry (that would orphan
      // tus resources). A terminal failure after its retries throws → the caller marks it 'error'.
      const minted = await withRetry(() =>
        callApi<{ uploadUrl: string; assetId: string; protocol?: string }>('media-upload-url', {
          method: 'POST',
          body: JSON.stringify({ clientId: client.id, kind, fileSize: file.size, fileName: file.name }),
        }),
      );
      // (a) live % → the store (label + fractional bar), not just captured-for-failures.
      await uploadVideoTus(minted.uploadUrl, file, (pct) => setItem(key, { status: 'uploading', detail: `${pct}%`, progress: pct }));
      assetId = minted.assetId;
    } else {
      // #GR9g: image → mint + single POST to Cloudflare, RETRIED together on transient failure. A
      // failed attempt re-mints a fresh single-use URL — no orphan asset, since nothing is
      // catalogued until the upload actually succeeds.
      assetId = await withRetry(async () => {
        const minted = await callApi<{ uploadUrl: string; assetId: string }>('media-upload-url', {
          method: 'POST',
          body: JSON.stringify({ clientId: client.id, kind }),
        });
        const fd = new FormData();
        fd.append('file', file, file.name);
        const up = await fetch(minted.uploadUrl, { method: 'POST', body: fd }); // straight to Cloudflare
        if (!up.ok) throw new Error(`Cloudflare upload failed (${up.status})`);
        return minted.assetId;
      });
    }

    setItem(key, { status: 'cataloguing' });
    // The asset is on Cloudflare; retry only the catalogue write (stable assetId → no
    // re-upload) so a transient function/DB blip doesn't lose a landed image.
    const { item } = await withRetry(() =>
      callApi<{ item: { id: string } }>('media-create', {
        method: 'POST',
        body: JSON.stringify({
          clientId: client.id,
          kind,
          assetId,
          alt: '', // set later via "Apply metadata from a CSV" (the single metadata door)
          groupId: groupId || null, // pre-assign to the chosen gallery (#8); '' = loose pool
          ...(dims ? { width: dims.width, height: dims.height } : {}), // #11a
          originalFilename: file.name,
        }),
      }),
    );

    if (!ingestedRef.current) {
      ingestedRef.current = true;
      onIngested(); // mark the step done on the first real catalogued asset
    }

    if (kind === 'video') {
      setItem(key, { status: 'finalizing', detail: 'encoding → MP4' });
      const started = Date.now();
      while (Date.now() - started < 20 * 60 * 1000) {
        await sleep(6_000);
        let st: { ready: boolean; mp4Ready: boolean };
        try {
          st = await callApi('media-finalize-video', {
            method: 'POST',
            body: JSON.stringify({ clientId: client.id, mediaId: item.id }),
          });
        } catch {
          continue; // transient — keep polling
        }
        if (st.mp4Ready) break;
      }
    }
    setItem(key, { status: 'done' });
  }

  // One item end-to-end. NEVER throws — records a terminal outcome ('done' via
  // ingestOne, else 'error') into `results` so the batch reconciliation is authoritative
  // (a failure can't masquerade as success, and can't halt the pool).
  async function processOne(key: string, results: Record<string, 'done' | 'error'>) {
    const ctx = itemCtx.get(key);
    if (!ctx) {
      results[key] = 'error';
      return;
    }
    // (b) Count this item in-flight for the WHOLE pipeline (upload → catalogue → finalize) — the
    // module-level counter drives the reload guard independent of any component state, so the
    // "don't reload mid-upload" warning holds even across a remount.
    beginInFlight();
    try {
      await ingestOne(ctx.file, ctx.kind, key, ctx.groupId);
      results[key] = 'done'; // ingestOne set status 'done'
      itemCtx.delete(key); // free the File once it's landed (only failures need retry)
    } catch (e) {
      setItem(key, { status: 'error', detail: friendlyError(e, (e as Error).message) });
      results[key] = 'error';
    } finally {
      endInFlight();
    }
  }

  // Run a set of queued keys through the bounded-concurrency pool, then reconcile:
  // count landed-vs-failed from the AUTHORITATIVE per-item results (not React state),
  // so a shortfall is always surfaced — never a bare "finished".
  async function runBatch(keys: string[]) {
    if (keys.length === 0) return;
    setBusy(true);
    const results: Record<string, 'done' | 'error'> = {};
    await runPool(keys, (key) => processOne(key, results), UPLOAD_CONCURRENCY);
    setBusy(false);
    // Reconcile + surface the honest count FIRST, from the authoritative per-item results — so a
    // shortfall ALWAYS reaches the designer even if the catalogue reload below fails (e.g. the
    // network is still down after a mid-upload drop). Previously the reload ran first and, on a
    // throw, swallowed the toast → a silent partial loss (#GR9g / David's 15-of-31).
    const failed = keys.filter((k) => results[k] !== 'done').length;
    const ok = keys.length - failed;
    setToast(
      failed === 0
        ? { message: `${ok} of ${keys.length} uploaded.`, kind: 'success' }
        : { message: `${ok} of ${keys.length} uploaded · ${failed} failed — use “Retry failed” below.`, kind: 'error' },
    );
    // Refresh the grid (landed items + the structuring view). Best-effort — never let a reload
    // error hide the count above.
    try {
      await loadCatalogue();
      onCatalogueChange?.();
    } catch {
      /* grid refresh is best-effort; the honest count already reached the designer */
    }
  }

  async function onFiles(files: File[]) {
    if (liveBlocked || files.length === 0) return;
    const assets = files.filter((f) => kindOf(f) !== null);
    if (assets.length === 0) {
      // Slice 1d: the uploader takes media only — a metadata CSV/JSON goes through the single
      // "Apply metadata from a CSV" door below the galleries (not two confusing drop points).
      setToast({ message: 'This takes photos & video. To set titles / alt / SEO from a CSV, use “Apply metadata from a CSV” below.', kind: 'error' });
      return;
    }
    // Capture the chosen gallery for the whole batch (so changing it mid-upload
    // doesn't split a batch across groups).
    const activeGroupId = uploadGroupId;

    const items: QueueItem[] = assets.map((f, i) => ({
      key: `${Date.now()}-${i}-${f.name}`,
      name: f.name,
      kind: kindOf(f)!,
      status: 'queued',
    }));
    // Remember each item's File + kind + target group so "Retry failed" can re-upload
    // exactly the ones that failed.
    items.forEach((it, i) => itemCtx.set(it.key, { file: assets[i], kind: it.kind, groupId: activeGroupId }));
    setQueue((q) => [...q, ...items]);
    await runBatch(items.map((it) => it.key));
  }

  // #GR9g: re-upload ONLY the failed items (not delete-all-and-redo). Combined with
  // the retries above, the common path is zero failures; this is the safety net.
  async function retryFailed() {
    const keys = queue.filter((q) => q.status === 'error' && itemCtx.has(q.key)).map((q) => q.key);
    if (keys.length === 0) return;
    keys.forEach((k) => setItem(k, { status: 'queued', detail: undefined }));
    await runBatch(keys);
  }

  async function editAlt(id: string, alt: string) {
    try {
      await callApi('media-update', { method: 'POST', body: JSON.stringify({ clientId: client.id, mediaId: id, alt }) });
      setCatalogue((c) => c.map((r) => (r.id === id ? { ...r, alt } : r)));
    } catch (e) {
      setToast({ message: friendlyError(e, (e as Error).message), kind: 'error' });
    }
  }

  // Delete a mistaken upload (M4b #10) — confirm, tear down on CF + remove the row.
  async function removeMedia(id: string, label: string) {
    if (deletingRef.current) return; // a delete is already running — ignore the double-click
    if (!window.confirm(`Delete "${label}" from the catalogue? This removes the file from Cloudflare too and can't be undone.`)) return;
    deletingRef.current = true;
    setDeleting(true);
    try {
      await callApi('media-delete', { method: 'POST', body: JSON.stringify({ clientId: client.id, mediaId: id }) });
      await loadCatalogue();
      onCatalogueChange?.();
      setToast({ message: 'Deleted.', kind: 'success' });
    } catch (e) {
      setToast({ message: friendlyError(e, (e as Error).message), kind: 'error' });
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  }

  // Re-order: swap sort_order with the neighbour, persist both, reload.
  async function move(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= catalogue.length) return;
    const a = catalogue[idx];
    const b = catalogue[j];
    try {
      await Promise.all([
        callApi('media-update', { method: 'POST', body: JSON.stringify({ clientId: client.id, mediaId: a.id, sortOrder: b.sort_order }) }),
        callApi('media-update', { method: 'POST', body: JSON.stringify({ clientId: client.id, mediaId: b.id, sortOrder: a.sort_order }) }),
      ]);
      await loadCatalogue();
    } catch (e) {
      setToast({ message: friendlyError(e, (e as Error).message), kind: 'error' });
    }
  }

  // Compact progress (M4b #6/#7): a counter + current-item box instead of one line per
  // file (68 lines at Fred-scale). Derived from the queue; errors stay visible after.
  const total = queue.length;
  const finished = queue.filter((q) => q.status === 'done' || q.status === 'error').length;
  const doneCount = queue.filter((q) => q.status === 'done').length;
  const errored = queue.filter((q) => q.status === 'error');
  const current = queue.find((q) => q.status === 'uploading' || q.status === 'cataloguing' || q.status === 'finalizing');
  // (a) Include the IN-FLIGHT files' live % in the bar — else one big video shows 0% its whole
  // duration (finished/total = 0 until it lands). Each uploading item contributes progress/100.
  const activeFraction = queue
    .filter((q) => q.status === 'uploading')
    .reduce((s, q) => s + (q.progress ?? 0) / 100, 0);
  const pct = total ? Math.min(100, Math.round(((finished + activeFraction) / total) * 100)) : 0;
  const verb =
    current?.status === 'finalizing' ? 'finalizing video' : current?.status === 'cataloguing' ? 'cataloguing' : 'uploading';

  return (
    <div className="space-y-4">
      <Toast toast={toast} onClose={() => setToast(null)} />
      <p className="text-sm text-ink-2">
        Add the client’s real photos and video — the build uses them instead of placeholders.
        <b> Optional</b> — you can skip and add media later. Mixed media is fine: photos only, video
        only, or both. Set titles / alt / SEO after uploading with <b>Apply metadata from a CSV</b> below.
      </p>

      {liveBlocked ? (
        <p className="rounded border border-warning-bright bg-warning-bright/10 p-2 text-sm text-warning">🔒 Site is live/protected — uploads are off.</p>
      ) : (
        <>
          {client.is_live && (
            <p className="rounded border border-warning-bright bg-warning-bright/10 p-2 text-sm text-warning">
              Live — maintenance mode: uploads land on the preview branch; the live site changes only on a deliberate publish.
            </p>
          )}
          {/* Pre-assign uploads to a gallery (#8) — the whole batch lands in this group. */}
          {/* DS-sweep (2-4b): the inputs/selects here are colour-tokenised (border→border-line)
              but keep their compact geometry — the ui.ts `input` token (text-sm px-3 py-1.5) is
              not forced, matching 2-4a, so the dense media UI doesn't reflow. */}
          {groups.length > 0 && (
            <label className="flex flex-wrap items-center gap-2 text-sm text-ink-2">
              Upload into:
              <select
                value={uploadGroupId}
                onFocus={() => void loadGroups()}
                onChange={(e) => setUploadGroupId(e.target.value)}
                className="rounded border border-line px-2 py-1 text-sm"
              >
                <option value="">Unassigned (loose pool)</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({g.kind})
                  </option>
                ))}
              </select>
            </label>
          )}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              void onFiles(Array.from(e.dataTransfer.files));
            }}
            className={`rounded border-2 border-dashed p-6 text-center text-sm ${dragOver ? 'border-ink bg-paper-2' : 'border-line text-ink-2'}`}
          >
            Drag &amp; drop images and video here
            <div className="mt-2">
              {/* DS-sweep (2-4b): a <label> styled as a button (wraps a hidden file input) —
                  colour-tokenised + kept compact (text-xs); not converted to the btnGhost
                  token, which would enlarge it to text-sm and add button focus semantics to a label. */}
              <label className="cursor-pointer rounded border border-line bg-paper px-3 py-1.5 text-xs text-ink-2 hover:bg-paper-2">
                or choose files
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={(e) => void onFiles(Array.from(e.target.files ?? []))}
                />
              </label>
            </div>
          </div>

          {/* Compact progress — a counter + current-item box (not one line per file). */}
          {total > 0 && (busy || finished > 0) && (
            <div className="space-y-1 rounded border border-line bg-paper p-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-ink-2">
                  {finished}/{total}
                  {current ? (
                    <>
                      {' '}· {verb} <span className="font-mono text-ink-2">{current.name}</span>
                      {current.detail ? <span className="text-ink-2"> · {current.detail}</span> : null}
                    </>
                  ) : busy ? (
                    <> · finishing…</>
                  ) : (
                    <> · done</>
                  )}
                </span>
                <span className="text-ink-2">
                  {doneCount} done{errored.length ? ` · ${errored.length} failed` : ''}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded bg-paper-2">
                <div className={`h-full ${errored.length ? 'bg-danger-bright' : 'bg-success-bright'}`} style={{ width: `${pct}%` }} />
              </div>
              {/* #GR9g reconciliation: when the batch finishes with a shortfall, say so
                  plainly + let the designer retry ONLY the failed items. */}
              {!busy && errored.length > 0 && (
                <div className="mt-1 space-y-1 rounded border border-danger bg-danger-bright/10 p-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-danger">
                      ⚠️ {doneCount} uploaded · {errored.length} failed — not all images landed.
                    </span>
                    <button
                      onClick={() => void retryFailed()}
                      className={`${btnSolid} flex-none`}
                    >
                      <FlapText>Retry failed ({errored.length})</FlapText>
                    </button>
                  </div>
                  <details className="group">
                    <summary className="cursor-pointer list-none text-danger">▸ show the {errored.length} failed file{errored.length === 1 ? '' : 's'}</summary>
                    <ul className="mt-1 space-y-0.5">
                      {errored.map((it) => (
                        <li key={it.key} className="text-danger">
                          <span className="font-mono">{it.name}</span>
                          {it.detail ? ` — ${it.detail}` : ''}
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Catalogue — collapsible (M4b #6); open by default (Fleet/structuring pattern).
          Hidden when embedded above the structuring view (which is the canonical list). */}
      {!hideCatalogue && catalogue.length > 0 && (
        <details open className="group space-y-2">
          <summary className="cursor-pointer list-none text-sm font-semibold text-ink-2 hover:text-ink">
            <span className="group-open:hidden">▸ Catalogue ({catalogue.length})</span>
            <span className="hidden group-open:inline">▾ Catalogue ({catalogue.length})</span>
          </summary>
          <ul className="mt-2 space-y-2">
            {catalogue.map((r, i) => (
              <li key={r.id} className="flex items-start gap-3 rounded border border-line bg-paper p-2">
                <div className="h-14 w-20 flex-none overflow-hidden rounded bg-paper-2">
                  {(r.poster ?? r.url) && (
                    <img src={r.poster ?? r.url ?? ''} alt="" className="h-full w-full object-cover" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {/* DS-sweep (2-4b): the image/video kind chip was categorical colour
                        (blue/purple) with no home in the 13 tokens → neutralised to
                        bg-paper-2/text-ink-2; the chip text still names the kind. */}
                    <span className="rounded bg-paper-2 px-1.5 py-0.5 text-[10px] uppercase text-ink-2">
                      {r.kind}
                    </span>
                    {r.kind === 'video' && !r.url && <span className="text-[10px] text-info">MP4 finalizing…</span>}
                    {r.role && <span className="text-[10px] text-ink-2">{r.role}</span>}
                    {/* #GR11: show the filename so the designer can point the build
                        agent at a specific shot ("use commercial/<name>"). */}
                    {r.original_filename && (
                      <span className="truncate font-mono text-[10px] text-ink-2" title={r.original_filename}>
                        {r.original_filename}
                      </span>
                    )}
                  </div>
                  <input
                    defaultValue={r.alt}
                    placeholder="alt text (from manifest)"
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== r.alt) void editAlt(r.id, v);
                    }}
                    className="mt-1 w-full rounded border border-line px-2 py-1 text-xs"
                  />
                </div>
                <div className="flex flex-none flex-col gap-1">
                  <button onClick={() => void move(i, -1)} disabled={i === 0 || liveBlocked} className="rounded border border-line px-1.5 text-xs disabled:opacity-30">↑</button>
                  <button onClick={() => void move(i, 1)} disabled={i === catalogue.length - 1 || liveBlocked} className="rounded border border-line px-1.5 text-xs disabled:opacity-30">↓</button>
                  <button
                    onClick={() => void removeMedia(r.id, r.alt || r.kind)}
                    disabled={liveBlocked || deleting}
                    title="Delete this item"
                    className="rounded border border-line px-1.5 text-xs text-ink-2 hover:bg-danger-bright/10 hover:text-danger disabled:opacity-30"
                  >
                    🗑
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}

      {busy && <p className="text-xs text-ink-2">Uploading… keep this step open until it finishes.</p>}
    </div>
  );
}
